/**
 * Fleet device list loader — cursor pagination with legacy full-list fallback.
 *
 * R12-15f: when the API returns a ``page`` object, walk ``next_cursor`` sequentially;
 * otherwise return the single response (pre-15c servers). Bound to shell ``api()`` via
 * ``bindFleetListApi`` at dashboard build/runtime.
 *
 * CAP-API-1e-FE: preserve ``page.visible_total`` from paginated GET /devices (DEF-214 honesty).
 */

import { FLEET_PAGE_LIMIT } from "./device-presence.js";
import { buildContractPath } from "./api/contract-client.js";

/** Devices route first paint: one cursor page before explicit load-more (FE-3a). */
export const FLEET_DEVICES_INITIAL_PAGES = 1;

/**
 * Poll routes (overview/group/groups/site): **one page per tick** (CAP-API-DEF214-KILL-MERGE).
 * Never cursor-merge sample universes on poll — honesty via visible_total + Load more.
 */
export const FLEET_POLL_MAX_PAGES = 1;

/** @type {((path: string, opts?: object) => Promise<unknown>) | null} */
let _api = null;

/**
 * @param {(path: string, opts?: object) => Promise<unknown>} apiFn
 */
export function bindFleetListApi(apiFn) {
  _api = apiFn;
}

/**
 * Honest fleet size from paginated ``page`` or list envelope (CAP-API-1e).
 * @param {object | null | undefined} pageOrList
 * @returns {number | null}
 */
export function fleetVisibleTotalFrom(pageOrList) {
  if (!pageOrList || typeof pageOrList !== "object") return null;
  const direct = pageOrList.visible_total;
  if (direct != null && Number.isFinite(Number(direct))) return Math.max(0, Math.floor(Number(direct)));
  const page = pageOrList.page && typeof pageOrList.page === "object" ? pageOrList.page : null;
  if (page && page.visible_total != null && Number.isFinite(Number(page.visible_total))) {
    return Math.max(0, Math.floor(Number(page.visible_total)));
  }
  return null;
}

/**
 * @param {object} result
 * @returns {object}
 */
function attachFleetVisibleTotal(result) {
  if (!result || typeof result !== "object") return result;
  const vt = fleetVisibleTotalFrom(result.page) ?? fleetVisibleTotalFrom(result);
  if (vt != null) result.visible_total = vt;
  const page = result.page && typeof result.page === "object" ? result.page : null;
  if (page && page.visible_total_truncated) result.visible_total_truncated = true;
  return result;
}

/**
 * @returns {boolean}
 */
export function fleetListPaginationForcedOff() {
  try {
    return localStorage.getItem("CROC_FLEET_PAGINATION") === "0";
  } catch {
    return false;
  }
}

/**
 * @param {unknown} body
 * @returns {boolean}
 */
export function isPaginatedFleetBody(body) {
  return !!(body && typeof body === "object" && body.page && typeof body.page === "object");
}

/**
 * @param {boolean} live
 * @param {number} [limit]
 * @param {string | null} [cursor]
 * @param {{ groupKey?: string, ownerAdmin?: string } | null} [groupOpts]
 * @param {string | null} [searchQ]
 * @param {boolean} [otaPreview]
 * @returns {string}
 */
export function buildFleetDevicesListPath(live, limit = FLEET_PAGE_LIMIT, cursor = null, groupOpts = null, searchQ = null, otaPreview = false) {
  const query = { limit };
  if (cursor) query.cursor = cursor;
  if (live) query.skip_cache = "1";
  if (groupOpts && groupOpts.groupKey) {
    query.notification_group = String(groupOpts.groupKey);
    if (groupOpts.ownerAdmin) query.owner_admin = String(groupOpts.ownerAdmin);
  }
  const sq = String(searchQ || "").trim();
  if (sq) query.q = sq;
  if (otaPreview) query.ota_preview = "1";
  return buildContractPath("get__devices", { query });
}

/**
 * Single cursor page (no merge). Custody principals may 400 → caller handles fallback.
 *
 * @param {{ live?: boolean, limit?: number, cursor?: string | null, timeoutMs?: number, retries?: number, searchQ?: string | null, groupKey?: string, ownerAdmin?: string, otaPreview?: boolean }} [opts]
 * @returns {Promise<{ items: object[], page: object | null, paginated: boolean, visible_total?: number }>}
 */
export async function fetchFleetDevicesPage(opts = {}) {
  if (!_api) throw new Error("fetchFleetDevicesPage: api not bound");
  const live = !!opts.live;
  const limit = Number.isFinite(Number(opts.limit)) ? Number(opts.limit) : FLEET_PAGE_LIMIT;
  const timeoutMs = Number.isFinite(Number(opts.timeoutMs)) ? Number(opts.timeoutMs) : 20000;
  const retries = Number.isFinite(Number(opts.retries)) ? Number(opts.retries) : 2;
  const cursor = opts.cursor != null ? String(opts.cursor) : null;
  const searchQ = opts.searchQ != null ? String(opts.searchQ).trim() : "";
  const otaPreview = !!opts.otaPreview;
  const groupOpts = opts.groupKey
    ? { groupKey: String(opts.groupKey), ownerAdmin: opts.ownerAdmin ? String(opts.ownerAdmin) : undefined }
    : null;
  const path = buildFleetDevicesListPath(live, limit, cursor, groupOpts, searchQ || null, otaPreview);
  const body = await _api(path, { timeoutMs, retries });
  const paginated = isPaginatedFleetBody(body);
  const items = body && Array.isArray(body.items) ? body.items : [];
  const page = body && body.page && typeof body.page === "object" ? body.page : null;
  return attachFleetVisibleTotal({ items, page, paginated: !!paginated });
}

/**
 * @param {{ live?: boolean, limit?: number, maxPages?: number, timeoutMs?: number, retries?: number, searchQ?: string | null, groupKey?: string, ownerAdmin?: string, otaPreview?: boolean }} [opts]
 * @returns {Promise<{ items: object[], page?: object | null, pages_fetched?: number, truncated?: boolean, visible_total?: number }>}
 */
export async function fetchFleetDevicesList(opts = {}) {
  if (!_api) throw new Error("fetchFleetDevicesList: api not bound");
  const live = !!opts.live;
  const limit = Number.isFinite(Number(opts.limit)) ? Number(opts.limit) : FLEET_PAGE_LIMIT;
  const timeoutMs = Number.isFinite(Number(opts.timeoutMs)) ? Number(opts.timeoutMs) : 20000;
  const retries = Number.isFinite(Number(opts.retries)) ? Number(opts.retries) : 2;
  const otaPreview = !!opts.otaPreview;

  if (fleetListPaginationForcedOff()) {
    if (otaPreview) {
      throw new Error("otaPreview forbids legacy full-list");
    }
    const legacyPath = buildContractPath("get__devices", {
      query: live ? { skip_cache: "1" } : {},
    });
    const body = await _api(legacyPath, { timeoutMs, retries });
    const items = body && Array.isArray(body.items) ? body.items : [];
    return { items };
  }

  // DEF-214 KILL-MERGE: never walk unbounded cursors (missing maxPages ⇒ first page only).
  const maxPagesRaw = opts.maxPages;
  const maxPages = Number.isFinite(Number(maxPagesRaw)) && Number(maxPagesRaw) > 0
    ? Math.floor(Number(maxPagesRaw))
    : FLEET_DEVICES_INITIAL_PAGES;

  const merged = [];
  let cursor = null;
  let paginated = null;
  let pagesFetched = 0;
  let lastPage = null;
  let firstVisibleTotal = null;

  const fetchLegacyFullList = async () => {
    const legacyPath = buildContractPath("get__devices", {
      query: live ? { skip_cache: "1" } : {},
    });
    const body = await _api(legacyPath, { timeoutMs, retries });
    const items = body && Array.isArray(body.items) ? body.items : [];
    return { items };
  };

  const isCustodyPagination400 = (err) => {
    const msg = String((err && err.message) || err || "");
    return msg.startsWith("400") && msg.includes("pagination not available");
  };

  const groupOpts = opts.groupKey
    ? { groupKey: String(opts.groupKey), ownerAdmin: opts.ownerAdmin ? String(opts.ownerAdmin) : undefined }
    : null;
  const searchQ = opts.searchQ != null ? String(opts.searchQ).trim() : "";

  for (;;) {
    const path = buildFleetDevicesListPath(live, limit, cursor, groupOpts, searchQ || null, otaPreview);
    let body;
    try {
      body = await _api(path, { timeoutMs, retries });
    } catch (err) {
      if (cursor === null && isCustodyPagination400(err)) {
        // otaPreview forbids legacy full-list
        if (otaPreview) throw err;
        return fetchLegacyFullList();
      }
      throw err;
    }
    if (paginated === null) paginated = isPaginatedFleetBody(body);
    if (!paginated) {
      const items = body && Array.isArray(body.items) ? body.items : [];
      return { items, page: null, pages_fetched: 1, truncated: false };
    }
    pagesFetched += 1;
    if (body && Array.isArray(body.items)) {
      for (const it of body.items) merged.push(it);
    }
    const page = body && body.page && typeof body.page === "object" ? body.page : {};
    lastPage = page;
    if (firstVisibleTotal == null) {
      const vt0 = fleetVisibleTotalFrom(page);
      if (vt0 != null) firstVisibleTotal = vt0;
    }
    if (pagesFetched >= maxPages) {
      return attachFleetVisibleTotal({
        items: merged,
        page: lastPage,
        pages_fetched: pagesFetched,
        truncated: !!page.has_more,
        visible_total: firstVisibleTotal != null ? firstVisibleTotal : undefined,
      });
    }
    if (!page.has_more) break;
    const next = page.next_cursor != null ? String(page.next_cursor) : "";
    if (!next) break;
    cursor = next;
  }

  return attachFleetVisibleTotal({
    items: merged,
    page: lastPage,
    pages_fetched: pagesFetched,
    truncated: false,
    visible_total: firstVisibleTotal != null ? firstVisibleTotal : undefined,
  });
}

/** Group-scoped poll: server filter · hard one-page walk (KILL-MERGE; load-more owns deeper pages). */
export const GROUP_DEVICES_POLL_MAX_PAGES = 1;

/**
 * Hard-clamp requested page walks for poll APIs (callers cannot reopen multi-page merge).
 * @param {unknown} requested
 * @param {number} hardCap
 * @returns {number}
 */
function clampPollMaxPages(requested, hardCap) {
  const hard = Math.max(0, Math.floor(Number(hardCap)) || 0);
  if (requested == null || requested === "") return hard;
  const n = Math.floor(Number(requested));
  if (!Number.isFinite(n)) return hard;
  if (n <= 0) return 0;
  return Math.min(n, hard);
}

/**
 * @param {{ live?: boolean, limit?: number, maxPages?: number, timeoutMs?: number, retries?: number, groupKey?: string, ownerAdmin?: string }} [opts]
 * @returns {Promise<{ items: object[], page?: object | null, pages_fetched?: number, truncated?: boolean, visible_total?: number }>}
 */
export async function fetchGroupDevicesPoll(opts = {}) {
  const groupKey = String(opts.groupKey || "").trim();
  if (!groupKey) return fetchFleetDevicesPoll(opts);
  const cap = clampPollMaxPages(
    opts.maxPages != null ? opts.maxPages : GROUP_DEVICES_POLL_MAX_PAGES,
    GROUP_DEVICES_POLL_MAX_PAGES,
  );
  return fetchFleetDevicesList({
    ...opts,
    maxPages: cap,
    groupKey,
    ownerAdmin: opts.ownerAdmin,
  });
}

/**
 * One-page fleet load for overview/group/groups/site poll ticks (KILL-MERGE).
 *
 * @param {{ live?: boolean, limit?: number, maxPages?: number, timeoutMs?: number, retries?: number }} [opts]
 * @returns {Promise<{ items: object[], page?: object | null, pages_fetched?: number, truncated?: boolean, visible_total?: number }>}
 */
export async function fetchFleetDevicesPoll(opts = {}) {
  const cap = clampPollMaxPages(
    opts.maxPages != null ? opts.maxPages : FLEET_POLL_MAX_PAGES,
    FLEET_POLL_MAX_PAGES,
  );
  return fetchFleetDevicesList({ ...opts, maxPages: cap });
}

/**
 * Trunc / honesty banner — never imply loaded sample == universe (DEF-214 / CAP-API-1e-FE).
 *
 * @param {object | null | undefined} list
 * @param {string} [bannerId]
 * @returns {string}
 */
export function fleetPollTruncatedHtml(list, bannerId = "fleetTruncBanner") {
  if (!list) return "";
  const n = Array.isArray(list.items) ? list.items.length : 0;
  const vt = fleetVisibleTotalFrom(list);
  const truncated = !!list.truncated || (vt != null && n < vt);
  if (!truncated) return "";
  const pf = list.pages_fetched != null ? String(list.pages_fetched) : "?";
  const vtNote = vt != null
    ? `Boards on file: <strong>${vt}</strong>${list.visible_total_truncated ? " (count incomplete)" : ""}. `
    : "Board count on file unavailable. ";
  return (
    `<p class="badge offline" id="${bannerId}" data-fleet-truncated="1" data-fleet-visible-total="${vt != null ? String(vt) : ""}" style="margin:8px 0">` +
    `Showing <strong>${n}</strong> loaded board(s)` +
    (pf !== "?" ? ` · ${pf} page(s)` : "") +
    `. ${vtNote}` +
    `This is a page/sample — not the full fleet. Use Load more on <a href="#/group/_all">All boards</a>.` +
    `</p>`
  );
}

/**
 * Insert or remove truncated-fleet banner after first header/section in a route view.
 *
 * @param {ParentNode | null | undefined} view
 * @param {object | null | undefined} list
 * @param {string} [bannerId]
 */
export function syncFleetPollTruncBanner(view, list, bannerId = "fleetTruncBanner") {
  if (!view) return;
  const existing = view.querySelector(`#${bannerId}`);
  const html = fleetPollTruncatedHtml(list, bannerId);
  if (!html) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    if (existing.outerHTML !== html) existing.outerHTML = html;
    return;
  }
  const anchor = view.querySelector("header.page-head, section.card, .page-head");
  if (anchor) anchor.insertAdjacentHTML("afterend", html);
  else view.insertAdjacentHTML("afterbegin", html);
}
